import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Group,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconInfoCircle } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { ApiError} from '../../api/client';
import { api, unwrap } from '../../api/client';
import type { Aircraft } from '../../api/types';
import { useSession } from '../../app/session';
import { applyFieldErrors, notifyError, notifySaved } from '../../lib/notify';

const GLIDER_CATEGORIES = new Set(['GLIDER', 'TMG']);

type FormValues = {
  registration: string;
  competition_number: string;
  category: string;
  aircraft_class: string;
  manufacturer: string;
  model: string;
  variant: string;
  type_designator: string;
  serial_number: string;
  year_of_manufacture: number | '';
  seats: number | '';
  mtow_kg: number | '';
  engine_count: number | '';
  is_multi_pilot: boolean;
  can_tow: boolean;
  launch_capabilities: string[];
  owner_organisation: string;
  operator_organisation: string;
  home_base: string;
  status: string;
  notes: string;
};

/**
 * Creating or editing an aircraft.
 *
 * The counters an aircraft needs follow from its category, so the form does not ask for them:
 * the server creates the right set - airframe hours and landings for an aeroplane, launches
 * and gliding time for a sailplane - and they are edited on the counters tab afterwards.
 */
export function AircraftFormDrawer({
  opened,
  onClose,
  aircraft,
}: {
  opened: boolean;
  onClose: () => void;
  aircraft?: Aircraft;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { organisation, organisations, enumChoices } = useSession();
  const isEdit = Boolean(aircraft);

  const airports = useQuery({
    queryKey: ['organisations', 'airports'],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/organisations/', {
          params: { query: { kind: 'AIRPORT', page_size: 100 } },
        }),
      ),
    enabled: opened,
  });

  const form = useForm<FormValues>({
    initialValues: {
      registration: aircraft?.registration ?? '',
      competition_number: aircraft?.competition_number ?? '',
      category: aircraft?.category ?? 'AIRPLANE',
      aircraft_class: aircraft?.aircraft_class ?? '',
      manufacturer: aircraft?.manufacturer ?? '',
      model: aircraft?.model ?? '',
      variant: aircraft?.variant ?? '',
      type_designator: aircraft?.type_designator ?? '',
      serial_number: aircraft?.serial_number ?? '',
      year_of_manufacture: aircraft?.year_of_manufacture ?? '',
      seats: aircraft?.seats ?? 2,
      mtow_kg: aircraft?.mtow_kg ? Number(aircraft.mtow_kg) : '',
      engine_count: aircraft?.engine_count ?? 1,
      is_multi_pilot: aircraft?.is_multi_pilot ?? false,
      can_tow: aircraft?.can_tow ?? false,
      launch_capabilities: (aircraft?.launch_capabilities as string[] | undefined) ?? [],
      owner_organisation: String(aircraft?.owner_organisation ?? organisation?.id ?? ''),
      operator_organisation: String(aircraft?.operator_organisation ?? organisation?.id ?? ''),
      home_base: aircraft?.home_base ? String(aircraft.home_base) : '',
      status: aircraft?.status ?? 'AIRWORTHY',
      notes: aircraft?.notes ?? '',
    },
    validate: {
      registration: (value) => (value.trim() ? null : t('common.requiredField')),
      manufacturer: (value) => (value.trim() ? null : t('common.requiredField')),
      model: (value) => (value.trim() ? null : t('common.requiredField')),
      launch_capabilities: (value, values) =>
        GLIDER_CATEGORIES.has(values.category) && value.length === 0
          ? t('common.requiredField')
          : null,
    },
  });

  const isGlider = GLIDER_CATEGORIES.has(form.values.category);

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const body = {
        registration: values.registration.trim().toUpperCase(),
        competition_number: values.competition_number.trim(),
        category: values.category as Aircraft['category'],
        aircraft_class: values.aircraft_class.trim(),
        manufacturer: values.manufacturer.trim(),
        model: values.model.trim(),
        variant: values.variant.trim(),
        type_designator: values.type_designator.trim().toUpperCase(),
        serial_number: values.serial_number.trim(),
        year_of_manufacture: values.year_of_manufacture === '' ? null : values.year_of_manufacture,
        seats: values.seats === '' ? 1 : values.seats,
        mtow_kg: values.mtow_kg === '' ? null : String(values.mtow_kg),
        engine_count: values.engine_count === '' ? 0 : values.engine_count,
        is_multi_pilot: values.is_multi_pilot,
        can_tow: values.can_tow,
        launch_capabilities: isGlider ? values.launch_capabilities : [],
        owner_organisation: Number(values.owner_organisation),
        operator_organisation: Number(values.operator_organisation),
        home_base: values.home_base ? Number(values.home_base) : null,
        status: values.status as Aircraft['status'],
        notes: values.notes,
      };

      if (aircraft) {
        return unwrap(
          await api.PATCH('/api/v1/aircraft/{registration}/', {
            params: { path: { registration: aircraft.registration } },
            body,
          }),
        );
      }
      return unwrap(await api.POST('/api/v1/aircraft/', { body }));
    },
    onSuccess: async () => {
      notifySaved();
      await queryClient.invalidateQueries({ queryKey: ['aircraft'] });
      onClose();
      form.reset();
    },
    onError: (error: ApiError) => {
      if (!applyFieldErrors(error, form.setErrors)) notifyError(error);
    },
  });

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title={<Title order={3}>{isEdit ? t('action.edit') : t('aircraft.addAircraft')}</Title>}
    >
      <form onSubmit={form.onSubmit((values) => save.mutate(values))}>
        <Stack gap="md">
          <Group grow align="flex-start">
            <TextInput
              label={t('aircraft.registration')}
              required
              placeholder="4O-DAA"
              {...form.getInputProps('registration')}
              onChange={(event) =>
                form.setFieldValue('registration', event.currentTarget.value.toUpperCase())
              }
              styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
            />
            <Select
              label={t('aircraft.category')}
              required
              data={enumChoices('aircraft_category').map((choice) => ({
                value: choice.value,
                label: choice.label,
              }))}
              allowDeselect={false}
              {...form.getInputProps('category')}
            />
          </Group>

          <Group grow align="flex-start">
            <TextInput
              label={t('aircraft.manufacturer')}
              required
              {...form.getInputProps('manufacturer')}
            />
            <TextInput label={t('aircraft.model')} required {...form.getInputProps('model')} />
          </Group>

          <Group grow align="flex-start">
            <TextInput label={t('aircraft.variant')} {...form.getInputProps('variant')} />
            <TextInput
              label={t('logbook.aircraftType')}
              placeholder="C172"
              description={t('aircraft.icaoHint')}
              {...form.getInputProps('type_designator')}
            />
            <TextInput
              label={t('aircraft.class')}
              placeholder="SEP (land)"
              {...form.getInputProps('aircraft_class')}
            />
          </Group>

          {isGlider ? (
            <>
              <MultiSelect
                label={t('aircraft.launchCapabilities')}
                required
                data={enumChoices('launch_capability').map((choice) => ({
                  value: choice.value,
                  label: choice.label,
                }))}
                {...form.getInputProps('launch_capabilities')}
              />
              <TextInput
                label={t('aircraft.competitionNumber')}
                w={160}
                {...form.getInputProps('competition_number')}
              />
            </>
          ) : null}

          <Group grow align="flex-start">
            <TextInput
              label={t('aircraft.serialNumber')}
              {...form.getInputProps('serial_number')}
            />
            <NumberInput
              label={t('aircraft.yearOfManufacture')}
              min={1900}
              max={new Date().getFullYear() + 1}
              {...form.getInputProps('year_of_manufacture')}
            />
          </Group>

          <Group grow align="flex-start">
            <NumberInput label={t('aircraft.seats')} min={1} max={20} {...form.getInputProps('seats')} />
            <NumberInput
              label={t('aircraft.mtow')}
              min={0}
              decimalScale={1}
              {...form.getInputProps('mtow_kg')}
            />
            <NumberInput
              label={t('aircraft.engineCount')}
              min={0}
              max={4}
              {...form.getInputProps('engine_count')}
            />
          </Group>

          <Group grow align="flex-start">
            <Select
              label={t('aircraft.operator')}
              required
              data={organisations.map((entry) => ({
                value: String(entry.id),
                label: entry.name,
              }))}
              allowDeselect={false}
              {...form.getInputProps('operator_organisation')}
            />
            <Select
              label={t('aircraft.owner')}
              required
              data={organisations.map((entry) => ({
                value: String(entry.id),
                label: entry.name,
              }))}
              allowDeselect={false}
              {...form.getInputProps('owner_organisation')}
            />
          </Group>

          <Group grow align="flex-start">
            <Select
              label={t('aircraft.homeBase')}
              data={(airports.data?.results ?? []).map((entry) => ({
                value: String(entry.id),
                label: entry.identifier ? `${entry.identifier} – ${entry.name}` : entry.name,
              }))}
              clearable
              searchable
              {...form.getInputProps('home_base')}
            />
            <Select
              label={t('common.status')}
              data={enumChoices('aircraft_status').map((choice) => ({
                value: choice.value,
                label: choice.label,
              }))}
              allowDeselect={false}
              {...form.getInputProps('status')}
            />
          </Group>

          <Group>
            <Checkbox
              label={t('aircraft.multiPilot')}
              {...form.getInputProps('is_multi_pilot', { type: 'checkbox' })}
            />
            <Checkbox
              label={t('aircraft.canTow')}
              {...form.getInputProps('can_tow', { type: 'checkbox' })}
            />
          </Group>

          <Textarea label={t('common.notes')} minRows={2} {...form.getInputProps('notes')} />

          {!isEdit ? (
            <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />} fz="sm">
              {t('aircraft.counters')}: {t('aircraft.adjustCounterHint')}
            </Alert>
          ) : null}

          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              {t('action.cancel')}
            </Button>
            <Button type="submit" loading={save.isPending}>
              {t('action.save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}
